"use client";
import { useMemo } from "react";
import { trpc } from "./trpc.client";
import toast from "react-hot-toast";
import { trpcStandalone } from "./trpcStandalone";

const thingToString = (thing: unknown): string => {
  if (typeof thing === "string") {
    return thing;
  }
  if (thing instanceof Error) {
    return `Error ${thing.name}: ${thing.message}\n${thingToString(thing.cause ?? "unknown cause")}\n${thing.stack ?? "no stack"}`;
  }
  try {
    return JSON.stringify(thing);
  } catch {
    return String(thing);
  }
};

const argsToString = (args: unknown[]) => {
  return args.map(thingToString).join(" ");
};

export const useLogger = () => {
  const clientLog = trpc.clientLog.log.useMutation();

  // break out the mutateAsync for stability
  const mutateAsync = clientLog.mutateAsync;
  const logger = useMemo(() => {
    return {
      error: (...args: unknown[]) => {
        const message = argsToString(args);
        console.error(message);
        mutateAsync({
          message,
          level: "error",
          url: window.location.href,
          createdAt: new Date().toISOString(),
          buildTime:
          (import.meta.env.VITE_BUILD_TIME as string | undefined) ?? "unknown",
        }).catch((err: unknown) => {
          console.error("Error logging error to clientLog", err);
        });
      },
      info: (...args: unknown[]) => {
        const message = argsToString(args);
        console.log(message);
        mutateAsync({
          message,
          level: "info",
          url: window.location.href,
          createdAt: new Date().toISOString(),
          buildTime:
          (import.meta.env.VITE_BUILD_TIME as string | undefined) ?? "unknown",
        }).catch((err: unknown) => {
          console.error("Error logging info to clientLog", err);
        });
      },
    };
  }, [mutateAsync]);
  return logger;
};

/**
 * This function returns a function that can be used to handle errors in a try/catch block.
 * @param context - A string describing the context in which the error occurred. Will be shown to the user with the prefix "Whoops, something went wrong while {context}."
 * @param args - Any additional arguments that will be logged with the error.
 * @returns A function that can be used to handle errors in a try/catch block.
 */
export const errHandler =
  (context: string, ...args: unknown[]) =>
  (e: unknown) => {
    // Alert the user of the error and log it to our backend
    toast.error(
      `Whoops, something went wrong while ${context}. We've notified the SimpleIEP team.`,
    );
    console.error(`Error while ${context}`, e);

    // Log the error to the clientLog table
    trpcStandalone.clientLog.log
      .mutate({
        message: `Error while ${context}: ${e instanceof Error ? e.message : "Unknown error"}. ${argsToString(
          args,
        )}`,
        level: "error",
        url: window.location.href,
        createdAt: new Date().toISOString(),
        meta: {
          args,
        },
        buildTime:
          (import.meta.env.VITE_BUILD_TIME as string | undefined) ?? "unknown",
      })
      .catch((err: unknown) => {
        console.error(
          "Error logging error to clientLog with standalone client in errHandler",
          err,
        );
      });

    // rethrow the error with the description, keeping the original stack trace and cause
    // const msg = e instanceof Error ? e.message : "Unknown error";
    // throw new Error(`Error while ${context}: ${msg}`, {
    //   cause: e,
    // });
  };

/**
 * Similar to errHandler but does not show a toast to the user.
 * Use this for errors that should be logged but not shown to the user.
 * @param context - A string describing the context in which the error occurred
 * @param args - Any additional arguments that will be logged with the error
 * @returns A function that can be used to handle errors in a try/catch block
 */
export const errHandlerWithoutUserToast =
  (context: string, ...args: unknown[]) =>
  (e: unknown) => {
    console.error(`Error while ${context}`, e);

    // Log the error to the clientLog table
    trpcStandalone.clientLog.log
      .mutate({
        message: `Silent error while ${context}: ${e instanceof Error ? e.message : "Unknown error"}. ${argsToString(
          args,
        )}`,
        level: "error",
        url: window.location.href,
        createdAt: new Date().toISOString(),
        meta: {
          args,
        },
        buildTime:
          (import.meta.env.VITE_BUILD_TIME as string | undefined) ?? "unknown",
      })
      .catch((err: unknown) => {
        console.error(
          "Error logging error to clientLog with standalone client in errHandlerWithoutUserToast",
          err,
        );
      });
  };
